import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SatepsiTest } from '@/api/satepsi';
import ConcludeAnimation from '@/components/animations/ConcludeAnimation';
import { Warning2, Spinner, SearchNormal1 } from '@/components/icons';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import FilterPopover from '@/components/ui/FilterPopover';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useDebounce } from '@/hooks/use-debounce';
import { useSatepsiTests } from '@/hooks/use-satepsi-tests';
import { capture } from '@/lib/posthog';
import { displayAgeRange } from '@/utils/displayAgeRange';
import { toTitleCase } from '@/utils/toTitleCase';
import { Test } from '../reports/types';
import Loading from '../ui/Loading';
import TestSelectorEmptyState from './TestSelectorEmptyState';

/** The two ways out of the catalog, for an instrument that is not in it. */
export type InstrumentFallbackRoute = 'tool_test_free' | 'tool_test_paste';

/** What the user had typed and filtered, so the way back can restore it. */
export type TestSelectorFilters = {
    query: string;
    domainId: string;
    includeUnfavorable: boolean;
};

interface TestSelectorProps {
    onSelectTests: (tests: Test[]) => void;
    patientAge?: number | string;
    onClose: () => void;
    alreadyAddedTestIds?: ReadonlySet<string>;
    /**
     * Leaves the catalog by one of the other two routes, carrying the filters
     * so the name can be prefilled and the way back can restore this view.
     *
     * Absent when the picker was opened to fill a table that already exists:
     * a free instrument there would build a second group and strand the empty
     * one above it.
     */
    onFallbackRoute?: (route: InstrumentFallbackRoute, filters: TestSelectorFilters) => void;
    /** Restores a view left behind — the "Voltar" of the other two routes. */
    initialFilters?: TestSelectorFilters;
}

const DEFAULT_FILTERS: TestSelectorFilters = {
    query: '',
    domainId: 'all',
    includeUnfavorable: false,
};

const EMPTY_TEST_ID_SET = new Set<string>();

const normalizeStatus = (status?: string) =>
    status
        ?.toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

const getStatusChipStyle = (status?: string) => {
    const normalizedStatus = normalizeStatus(status);

    if (normalizedStatus?.includes('desfavoravel')) {
        return {
            label: 'Desfavorável',
            className: 'bg-destructive/10 text-destructive border-destructive/30',
        };
    }

    if (normalizedStatus?.includes('favoravel')) {
        return {
            label: 'Favorável',
            className: 'bg-info-subtle text-info border-info/30',
        };
    }

    if (normalizedStatus?.includes('em avaliacao')) {
        return {
            label: 'Em avaliação',
            className: 'bg-muted text-foreground border-border-strong',
        };
    }

    return null;
};

export default function TestSelector({
    onSelectTests,
    patientAge,
    onClose,
    alreadyAddedTestIds = EMPTY_TEST_ID_SET,
    onFallbackRoute,
    initialFilters = DEFAULT_FILTERS,
}: TestSelectorProps) {
    const [searchQuery, setSearchQuery] = useState(initialFilters.query);
    const debouncedSearchQuery = useDebounce(searchQuery, 500);
    const [selectedTestsById, setSelectedTestsById] = useState<Map<string, Test>>(() => new Map());
    const [selectedDomainId, setSelectedDomainId] = useState(initialFilters.domainId);
    const [includeUnfavorable, setIncludeUnfavorable] = useState(initialFilters.includeUnfavorable);

    const normalizeDomain = (value: string) =>
        value
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '-');

    // Map the selected domain ID back to the raw construct value for the API
    const domainIdToRawMapRef = useRef<Map<string, string>>(new Map());
    const constructParam =
        selectedDomainId !== 'all' ? domainIdToRawMapRef.current.get(selectedDomainId) : undefined;

    const debouncedConstruct = useDebounce(constructParam, 300);

    const {
        tests: satepsiTests,
        domains,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        error,
        sentinelRef,
    } = useSatepsiTests({
        query: debouncedSearchQuery,
        construct: debouncedConstruct,
    });

    const availableTests = useMemo<Test[]>(() => {
        if (!satepsiTests) return [];

        return satepsiTests.map((t: SatepsiTest) => {
            const domain =
                Array.isArray(t.constructs) && t.constructs.length > 0 ? t.constructs : ['Outros'];

            return {
                id: t.id,
                name: t.name,
                domain: domain,
                domainId: domain.map((d: string) => normalizeDomain(d)),
                authors: t.authors.map((author: string) => toTitleCase(author)),
                publisher: t.publisher ? toTitleCase(t.publisher) : undefined,
                status: t.status as Test['status'],
                ageRange: t.ageRange,
                defaultBlocks: t.defaultBlocks,
            };
        });
    }, [satepsiTests]);

    const domainOptions = useMemo(() => {
        const byId = new Map<string, string>();
        domains.forEach((raw) => {
            const id = normalizeDomain(raw);
            if (!byId.has(id)) byId.set(id, raw);
        });

        domainIdToRawMapRef.current = byId;
        return Array.from(byId, ([id, raw]) => ({ id, raw }));
    }, [domains]);

    const filteredTests = useMemo(() => {
        return availableTests.filter((test) => {
            const matchesStatus =
                includeUnfavorable || !normalizeStatus(test.status)?.includes('desfavoravel');

            return matchesStatus;
        });
    }, [availableTests, includeUnfavorable]);

    /**
     * The catalog has the instruments; the "desfavoráveis" checkbox is hiding
     * them. Offering to build one by hand here would be wrong — the answer is
     * the filter, not a new instrument.
     */
    const hiddenByUnfavorableFilter = availableTests.length > 0 && filteredTests.length === 0;

    /**
     * A selection survives a search, so the user can be sitting on checked
     * instruments they can no longer see. Leaving unmounts this list and drops
     * them without a word, so the way out closes until they are added.
     */
    const hasPendingSelection = selectedTestsById.size > 0;
    const canLeaveCatalog = Boolean(onFallbackRoute) && !hasPendingSelection;

    // Confetti — play only once when user reaches the end
    const allLoaded = !isLoading && !hasNextPage && filteredTests.length > 0;
    const [showConfetti, setShowConfetti] = useState(false);
    const hasShownConfettiRef = useRef(false);

    useEffect(() => {
        if (allLoaded && !hasShownConfettiRef.current) {
            hasShownConfettiRef.current = true;
            setShowConfetti(true);
        }
    }, [allLoaded]);

    const isTestSelected = (test: Test) => {
        return selectedTestsById.has(test.id);
    };

    const toggleTest = (test: Test) => {
        if (alreadyAddedTestIds.has(test.id)) return;

        setSelectedTestsById((prev) => {
            const next = new Map(prev);

            if (next.has(test.id)) {
                next.delete(test.id);
            } else {
                next.set(test.id, test);
            }

            return next;
        });
    };

    const handleConfirm = () => {
        onSelectTests(Array.from(selectedTestsById.values()));
        handleClose();
    };

    /**
     * Hands the user over to one of the other two routes. Deliberately does not
     * close anything: the modal provider has a single slot, so the caller
     * replaces this dialog's content in place, and calling `onClose` too would
     * race a 300ms teardown against that swap.
     */
    const handleFallbackRoute = (route: InstrumentFallbackRoute) => {
        capture('report_instrument_add_clicked', {
            surface: 'catalog_empty_state',
            instrument_source: route === 'tool_test_free' ? 'free_mode' : 'clipboard',
        });

        onFallbackRoute?.(route, {
            // Raw, not debounced: past the loading guard below the two agree,
            // and while the user is still typing the raw value is the one they
            // mean.
            query: searchQuery.trim(),
            domainId: selectedDomainId,
            includeUnfavorable,
        });
    };

    const handleClose = () => {
        setSelectedTestsById(new Map());
        setSearchQuery('');
        setSelectedDomainId('all');
        setIncludeUnfavorable(false);
        onClose();
    };

    const isAgeCompatible = (test: Test) => {
        if (!patientAge || !test.ageRange) return true;

        const age = typeof patientAge === 'string' ? parseInt(patientAge, 10) : patientAge;
        if (isNaN(age)) return true;

        const { from, to } = test.ageRange;

        if (from === 0 && to === 0) return true;
        if (from && !to) return age >= from;

        return age >= from && age <= to;
    };

    return (
        <div className="flex size-full min-h-0 flex-col">
            <div className="shrink-0 space-y-2 px-6 pb-4">
                <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                        <SearchNormal1 className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            autoFocus
                            placeholder="Digite o nome do instrumento..."
                            value={searchQuery}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setSearchQuery(e.target.value)
                            }
                            className="rounded-lg border-border-strong pl-9"
                        />
                    </div>
                    <div className="flex h-10 shrink-0 items-center gap-2 rounded-lg border border-border-strong bg-card px-3">
                        <Checkbox
                            id="include-unfavorable"
                            checked={includeUnfavorable}
                            onCheckedChange={(checked) => setIncludeUnfavorable(checked === true)}
                            className="size-4"
                        />
                        <label
                            htmlFor="include-unfavorable"
                            className="cursor-pointer select-none whitespace-nowrap text-sm text-muted-foreground"
                        >
                            Incluir desfavoráveis
                        </label>
                    </div>
                    <FilterPopover
                        options={domainOptions.map((d) => ({
                            value: d.id,
                            label: d.raw,
                        }))}
                        value={selectedDomainId}
                        onApply={setSelectedDomainId}
                        label="Domínio"
                        placeholder="Selecione um domínio"
                    />
                </div>
            </div>

            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-6 py-2 pr-4">
                {error && (
                    <Alert variant="destructive">
                        Erro ao carregar instrumentos. Tente novamente mais tarde.
                    </Alert>
                )}

                {isLoading && (
                    <div className="py-6">
                        <Loading className="h-full" />
                    </div>
                )}

                {!isLoading && !error && filteredTests.length === 0 && (
                    <TestSelectorEmptyState
                        hiddenByUnfavorableFilter={hiddenByUnfavorableFilter}
                        canLeaveCatalog={canLeaveCatalog}
                        hasPendingSelection={hasPendingSelection}
                        onIncludeUnfavorable={() => setIncludeUnfavorable(true)}
                        onFallbackRoute={handleFallbackRoute}
                    />
                )}

                <TooltipProvider delayDuration={0}>
                    {!isLoading &&
                        filteredTests.map((test) => {
                            const isAlreadyAdded = alreadyAddedTestIds.has(test.id);
                            const isSelected = isAlreadyAdded || isTestSelected(test);
                            const compatible = isAgeCompatible(test);
                            const statusChip = getStatusChipStyle(test.status);

                            const testRow = (
                                <div className="space-y-1">
                                    <div
                                        role="checkbox"
                                        aria-checked={isSelected}
                                        aria-disabled={isAlreadyAdded || undefined}
                                        tabIndex={isAlreadyAdded ? -1 : 0}
                                        onClick={() => toggleTest(test)}
                                        onKeyDown={(event: React.KeyboardEvent) => {
                                            if (event.key === ' ' || event.key === 'Enter') {
                                                event.preventDefault();
                                                toggleTest(test);
                                            }
                                        }}
                                        className={`flex select-none items-center gap-3 rounded-xl border-2 p-4 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                                            isAlreadyAdded ? 'cursor-default' : 'cursor-pointer'
                                        } ${
                                            isSelected
                                                ? 'border-info bg-info-subtle'
                                                : !compatible
                                                  ? 'border-warning/40 bg-warning-subtle'
                                                  : 'border-border bg-card hover:border-border-strong'
                                        }`}
                                    >
                                        {/* Presentational: the whole card is the control, so the
                                            box must not swallow the click or take its own focus. */}
                                        <Checkbox
                                            checked={isSelected}
                                            disabled={isAlreadyAdded}
                                            tabIndex={-1}
                                            aria-hidden
                                            className="pointer-events-none size-5"
                                        />

                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <div className="font-medium text-foreground">
                                                    {test.name}
                                                </div>
                                                {statusChip && (
                                                    <Badge
                                                        variant="outline"
                                                        className={statusChip.className}
                                                    >
                                                        {statusChip.label}
                                                    </Badge>
                                                )}
                                                {!compatible && (
                                                    <span className="flex items-center gap-1.5 rounded-full border border-warning/30 bg-warning-subtle px-2 py-0.5 text-xs font-medium text-warning shadow-sm">
                                                        <Warning2 className="size-3.5" />
                                                        Idade incompatível
                                                    </span>
                                                )}
                                            </div>
                                            {test.authors && test.authors?.length > 0 && (
                                                <div className="flex">
                                                    <span className="pt-2 text-sm text-muted-foreground">
                                                        Autores: {test?.authors.join(', ')}
                                                    </span>
                                                </div>
                                            )}
                                            {test.publisher && (
                                                <div className="flex">
                                                    <span className="pt-2 text-sm text-muted-foreground">
                                                        Editora: {test.publisher}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="text-sm text-muted-foreground">
                                                {test.domain.length > 3
                                                    ? `${test.domain.slice(0, 3).join(', ')}, ...`
                                                    : test.domain.join(', ')}
                                                {test.ageRange && (
                                                    <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                                                        {displayAgeRange(test.ageRange)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );

                            if (!isAlreadyAdded) {
                                return <React.Fragment key={test.id}>{testRow}</React.Fragment>;
                            }

                            return (
                                <Tooltip key={test.id}>
                                    <TooltipTrigger asChild>{testRow}</TooltipTrigger>
                                    <TooltipContent side="top">Já adicionado</TooltipContent>
                                </Tooltip>
                            );
                        })}
                </TooltipProvider>

                {/* Infinite scroll sentinel */}
                {!isLoading && !error && filteredTests.length > 0 && (
                    <div ref={sentinelRef} className="py-1" />
                )}

                {isFetchingNextPage && (
                    <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground">
                        <Spinner className="size-4 animate-spin" />
                        <span className="text-sm">Carregando mais instrumentos...</span>
                    </div>
                )}

                {allLoaded && (
                    <div className="py-3 text-center">
                        {showConfetti && (
                            <div className="pointer-events-none fixed bottom-4 left-1/2 z-50 size-48 -translate-x-1/2">
                                <ConcludeAnimation />
                            </div>
                        )}
                        <span className="text-xs text-muted-foreground">
                            Todos os instrumentos foram carregados
                        </span>
                    </div>
                )}
            </div>

            <div className="flex shrink-0 items-center justify-end gap-3 border-t bg-card px-6 py-4">
                <Button variant="outline" onClick={handleClose}>
                    Cancelar
                </Button>
                <Button onClick={handleConfirm} disabled={selectedTestsById.size === 0}>
                    Adicionar ({selectedTestsById.size})
                </Button>
            </div>
        </div>
    );
}
